import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Typography, Link } from "@castlery/fortress";
import { ChevronDown, ChevronUp } from "lucide-react";
import { ErrorBoundary } from "react-error-boundary";
import { ErrorContent } from "./Message";

interface CampaignData {
  id: string;
  title: string;
  start_time: string;
  end_time: string;
  description: string;
}

const formatDateRange = (startTime: string, endTime: string): string => {
  try {
    const startDate = new Date(startTime);
    const endDate = new Date(endTime);

    // 格式化日期为 "DD MMM YYYY" 格式
    const formatDate = (date: Date) => {
      const day = date.getDate().toString().padStart(2, "0");
      const month = date.toLocaleDateString("en-US", { month: "short" });
      const year = date.getFullYear();
      return `${day} ${month} ${year}`;
    };

    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  } catch (error) {
    console.error("Error formatting date range:", error);
    return `${startTime} - ${endTime}`;
  }
};

// 单个促销活动卡片组件
const CampaignCardItem: React.FC<{ item: CampaignData }> = ({ item }) => {
  const { title, description } = item;

  return (
    <div className="p-4 bg-[#F6F3E7]">
      {/* 标题 */}
      <div className="mb-1">
        <Typography level="body2" className="text-[#844025]">
          {title}
        </Typography>
      </div>

      {/* 描述 */}
      <div>
        <Typography level="caption1">{description}</Typography>
      </div>
    </div>
  );
};

const CampaignCardWithoutErrorBoundary = (props: {
  data: { campaigns: CampaignData[] };
}) => {
  const { campaigns = [] } = props.data;

  const [isExpanded, setIsExpanded] = useState(false);

  const maxVisibleItems = 3;
  const hasMoreItems = campaigns.length > maxVisibleItems;
  const visibleItems = isExpanded
    ? campaigns
    : campaigns.slice(0, maxVisibleItems);

  if (campaigns.length === 0) {
    return null;
  }

  return (
    <Card className="w-full rounded-md rounded-tl-none border border-[#F6F3E7] bg-[#FBF9F4] text-[#3C101E]">
      <CardContent className="p-3 flex flex-col gap-3 font-aime">
        {visibleItems.map((item, index) => (
          <CampaignCardItem key={`campaign-${item.id}-${index}`} item={item} />
        ))}

        {/* View all campaign 按钮 */}
        {hasMoreItems && (
          <div className="flex items-center justify-center">
            <Link
              variant="secondary"
              size="xs"
              endDecorator={
                isExpanded ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )
              }
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? "View less promotions" : "View all promotions"}
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const CampaignCard = (props: {
  data: { campaigns: CampaignData[] };
}) => {
  return (
    <ErrorBoundary fallback={<ErrorContent position="left" />}>
      <CampaignCardWithoutErrorBoundary {...props} />
    </ErrorBoundary>
  );
};
